"use client"

import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useGSAP } from "@gsap/react"
import Image from "next/image"
import { useHoverContext } from "@/contexts/HoverContext"
import { useTransitionRouter } from "next-view-transitions"
import { slideInOut } from "@/app/lib/gsapAnimation"
import { usePathname } from "next/navigation"

const menuLinks = [
  { path: "/", label: "Home" },
  { path: "/work", label: "Work" },
  { path: "/about", label: "About" },
  { path: "/insights", label: "Insights" }
]

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

function Menu() {

  const pathname = usePathname()
  const transitionRouter = useTransitionRouter()
  const { setHoverTarget } = useHoverContext()
  const container = useRef()
  const menuRef = useRef()
  const tl = useRef()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMenuVisible, setIsMenuVisible] = useState(true)

  const cleanupAnimations = () => {
    if (typeof window !== "undefined") {
      try {
        const allTriggers = ScrollTrigger.getAll()
        allTriggers.forEach(trigger => {
          trigger.disable(true)
          trigger.kill(true)
        })
      } catch (error) {
        console.error("Error cleaning up animations:", error)
      }
    }
  }

  const toggleMenu = (link = "") => {
    if (link) {
      // Clean up ScrollTrigger animations before navigation but preserve menu animations
      cleanupAnimations()

      // Navigate after menu closes
      transitionRouter.push(link, { onTransitionReady: slideInOut })
      // Close the menu first
      setIsMenuOpen(false)
    } else {
      setIsMenuOpen(!isMenuOpen)
    }
  }

  useGSAP(
    () => {
      const lineCrossed = container.current.querySelector(".line-crossed")
      
      gsap.set(".menu-link-item-holder", { y: 200 })
      gsap.set(lineCrossed, {x: "100%", width: 0})

      tl.current = gsap
        .timeline({ paused: true })
        .to(".menu-overlay", {
          duration: 1,
          clipPath: "polygon(0% 0% , 100% 0%, 100% 100%, 0% 100%)",
          ease: "power4.inOut"
        })
        .to(".menu-link-item-holder", {
          y: "-2%",
          duration: 1,
          stagger: 0.1,
          ease: "power4.inOut",
          delay: -0.75 // start animation before the previous part finishes
        })
        .to(lineCrossed, { x: "0", width: "100%", duration: .25, ease: "power2.out" })
    },
    { scope: container, revertOnUpdate: true }
  )

  // Handle scroll visibility
  useEffect(() => {
    let lastScrollTop = 0
    let scrollTimeout
    let viewportHeight = window.innerHeight

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollingDown = scrollTop > lastScrollTop

      const nearBottom = scrollTop > document.body.scrollHeight - viewportHeight * 1.5



      // Clear any existing timeouts
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      // If we're scrolling down and past 100vh, hide the menu
      if (scrollingDown && scrollTop > viewportHeight && isMenuVisible && !isMenuOpen) {
        gsap.to(menuRef.current, {
          y: "-200px",
          duration: 0.5,
          ease: "power4.in",
          onComplete: () => setIsMenuVisible(false)
        })
      }

      // If we're scrolling up at any position, show the menu immediately
      else if (!scrollingDown && scrollTop < lastScrollTop && !isMenuOpen) {

      
        gsap.to(menuRef.current, {
          y: "0",
          duration: 0.35,
          ease: "power4.out",
          onComplete: () => setIsMenuVisible(true)
        })
      }

      // When scroll stops, set a timeout to show the menu after 1 or 2 seconds
      scrollTimeout = setTimeout(() => {
        if (!isMenuVisible && !isMenuOpen && !nearBottom) {
          gsap.to(menuRef.current, {
            y: "0",
            duration: 0.5,
            ease: "power2.out",
            onComplete: () => setIsMenuVisible(true)
          })
        }
      }, 1700) // 3 second delay

      lastScrollTop = scrollTop
    }

    // Add resize listener to update viewportHeight if window is resized
    const handleResize = () => {
      viewportHeight = window.innerHeight
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [isMenuVisible, isMenuOpen])

  useEffect(() => {
    if (isMenuOpen) {
      tl.current.play()
      setHoverTarget("menu-close")

      // Make sure menu is visible when opened
      if (!isMenuVisible) {
        gsap.to(menuRef.current, {
          y: "0%",
          duration: 0.3,
          ease: "power2.out",
          onComplete: () => {
            setIsMenuVisible(true)
          }
        })
      }
    } else {
      tl.current.reverse()
      setHoverTarget("menu-open")
      setIsMenuOpen(false)
    }
  }, [isMenuOpen])

  return (
    <div className="container mx-auto" ref={container}>
      <div ref={menuRef} className="fixed top-0 left-0 py-[1.25em] z-[2] w-full transition-transform">
        <div className="container mx-auto flex justify-between">
          <div className={`menu-logo ${pathname === "/" && "pointer-events-none"}`}>
            <a
              onClick={e => {
                e.preventDefault()
                if (isMenuOpen) {
                  // If menu is open, close it first
                  setIsMenuOpen(false)
                  // Wait for menu close animation to finish before navigating
                  setTimeout(() => {
                    cleanupAnimations()
                    transitionRouter.push("/", { onTransitionReady: slideInOut })
                  }, 10)
                } else {
                  // If menu is already closed, just clean up and navigate
                  cleanupAnimations()
                  setIsMenuOpen(false)
                  transitionRouter.push("/", { onTransitionReady: slideInOut })
                }
              }}
              title="Home page"
            >
              <div>{isMenuOpen ? <img src="/logo/makingyousmile-white.svg" alt="logo Making You Smile - Branding Agency" width={100} height={66} data-target="logo" /> : <img src="/logo/makingyousmile-color.svg" alt="logo Making You Smile - Branding Agency" width={100} height={66} data-target="logo" />}</div>
            </a>
          </div>
          <button
            className="menu-open font-bold"
            onClick={() => {
              toggleMenu()
            }}
            data-target={isMenuOpen ? "menu-close" : "menu-open"}
          >
            {isMenuOpen ? <span className="text-white">CLOSE</span> : <span>MENU</span>}
          </button>
        </div>
      </div>
      {/** OVERLAY */}
      <div className="menu-overlay">
        <div className="menu-copy flex items-center">
          <div className="menu-links group">
            {menuLinks.map((link, index) => {
              return (
                <div className={`menu-link-item relative transition-all duration-200 ease-linear group-hover:opacity-25 hover:!opacity-100 ${pathname !== link?.path ? "" : "pointer-events-none"}`} key={index}>
                  {pathname === link?.path && <div className="line-crossed absolute w-full h-[6px] top-1/2 -translate-y-1/2 -mt-1  z-[1] bg-lime-600"></div>}
                  <div className="menu-link-item-holder " onClick={() => toggleMenu(link?.path)}>
                    <a className={`menu-link pb-2 ${pathname === link?.path ? "text-lime-600" : "text-white"}`}>{link.label}</a>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Menu
